/**
 * useMessageMutations - React Query mutation hooks for message operations
 *
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { messageQueryKeys } from '../config/queryKeys';
import {
  createMessage,
  sendReply,
  sendSidebarMessage,
  updateMessageThread,
  updateReadPointer,
  inviteUsersToThread,
  changeMessageCase,
  cancelScheduledMessage,
} from '../services/messageService';
import { getTenantDetails } from 'utils/utils';

/**
 * Create a new message thread via API
 */
export function useCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => createMessage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.messages.all });
    },
  });
}

export function useSendReply() {
  const queryClient = useQueryClient();
  const userContext = useUserDetailsContext();

  return useMutation({
    mutationFn: (payload) => sendReply(payload),

    onMutate: async (variables) => {
      const threadId = variables.message_thread_id;
      if (!threadId) return {};

      const cacheKey = messageQueryKeys.threadReplies.byId(threadId);

      // Prevent in-flight refetches from overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: cacheKey });

      // Snapshot for rollback
      const previousData = queryClient.getQueryData(cacheKey);

      const optimisticSk = `optimistic-${Date.now()}`;

      const userDetails = userContext?.firmDetails || {};
      const optimisticReply = {
        sk: optimisticSk,
        pk: '',
        message_thread_id: threadId,
        message: variables.message || '',
        attachment: variables.attachment || [],
        event_attachment: variables.event_attachment || [],
        task_attachment: variables.task_attachment || [],
        author_details: {
          first_name: userDetails.first_name || '',
          last_name: userDetails.last_name || '',
          firm_user_id: userDetails.firm_user_id || '',
          contact_id: userDetails.contact_id || '',
          middle_name: userDetails.middle_name || '',
        },
        created_by_user_image: userDetails.profile_image || '',
        _optimistic: true,
        time: new Date().toISOString(),
        scheduled_send_at: variables.scheduled_send_at || null,
        scheduled_status: variables.scheduled_status || null,
      };

      queryClient.setQueryData(cacheKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        // Append to the first (most recent) page
        const updatedPages = [...oldData.pages];
        const firstPage = updatedPages[0];
        updatedPages[0] = {
          ...firstPage,
          data: {
            ...firstPage.data,
            replies: [optimisticReply, ...(firstPage.data?.replies || [])],
            count: (firstPage.data?.count || 0) + 1,
          },
        };

        return { ...oldData, pages: updatedPages };
      });

      return { previousData, optimisticSk, cacheKey };
    },

    onSuccess: (data, _variables, context) => {
      const { optimisticSk, cacheKey } = context || {};
      if (!cacheKey || !optimisticSk) return;
      const [tenantId] = getTenantDetails();

      const realReply = data?.data;

      queryClient.setQueryData(cacheKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        const updatedPages = oldData.pages.map((page) => {
          const replies = page?.data?.replies || [];

          return {
            ...page,
            data: {
              ...page.data,
              replies: replies.map((r) =>
                r.sk === optimisticSk
                  ? {
                      ...r,
                      _optimistic: false,
                      message_reply_id: realReply?.reply_id,
                      sk: `message_reply#${realReply?.reply_id}`,
                      pk: `${tenantId}#${realReply?.message_thread_id}`,
                      message_thread_id: realReply?.message_thread_id,
                    }
                  : r
              ),
            },
          };
        });

        return { ...oldData, pages: updatedPages };
      });
    },

    onError: (_error, _variables, context) => {
      const { previousData, cacheKey } = context || {};
      if (cacheKey && previousData !== undefined) {
        queryClient.setQueryData(cacheKey, previousData);
      }
    },
  });
}

/**
 * Send a sidebar message via API
 */
export function useSendSidebarMessage() {
  return useMutation({
    mutationFn: (payload) => sendSidebarMessage(payload),
  });
}

/**
 * Update message thread (mark read/unread, resolution status, assignment)
 */
export function useUpdateMessageThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, isLead }) => updateMessageThread(data, isLead),
    onSuccess: (data, variables) => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: messageQueryKeys.threadDetails.byId(variables?.data?.message_thread_id),
        });
      }, 1500);
    },
  });
}

/**
 * Update read pointer (unified read/unread for replies and sidebar messages)
 *
 * Optimistically updates the React Query cache instead of invalidating so the
 * UI reflects the change instantly without a refetch round-trip.
 *
 * message_type === "reply"   → updates is_read + is_new
 * message_type === "sidebar" → updates is_new_sidebar_message
 *
 * Both updates apply to:
 *  1. Every cached message-list page (partial key match on ['secure-messages', 'list'])
 *  2. The specific thread-details entry (exact key by message_thread_id)
 *
 * On API error the cache is rolled back to the snapshot captured in onMutate.
 *
 * @param {Object} payload
 * @param {string} payload.message_thread_id - Required
 * @param {string} payload.user_id           - Required (firm_user_id or contact_id)
 * @param {string} payload.message_type      - Required ("reply" | "sidebar")
 * @param {boolean} [payload.unread=false]   - true → mark unread, false → mark read
 */
export function useUpdateReadPointer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => updateReadPointer(payload),

    onMutate: async ({ message_thread_id, message_type, unread = false }) => {
      // Partial key covers all filter/pagination variants of the list
      const listPartialKey = ['secure-messages', 'list'];
      const threadKey = messageQueryKeys.threadDetails.byId(message_thread_id);

      // Cancel in-flight fetches so they don't overwrite our optimistic write
      await Promise.all([queryClient.cancelQueries({ queryKey: listPartialKey }), queryClient.cancelQueries({ queryKey: threadKey })]);

      // Snapshot for rollback
      const previousListQueries = queryClient.getQueriesData({ queryKey: listPartialKey });
      const previousThreadDetails = queryClient.getQueryData(threadKey);

      // ── Build the field patch based on message_type ──────────────────────
      const listItemPatch = (msg) => {
        if (msg?.message_thread_id !== message_thread_id) return msg;
        if (message_type === 'reply') {
          return { ...msg, is_read: !unread, is_new: false };
        }
        if (message_type === 'sidebar') {
          return { ...msg, is_new_sidebar_message: false };
        }
        return msg;
      };

      const threadDetailsPatch = (rawData) => {
        if (message_type === 'reply') {
          return { ...rawData, is_read: !unread, is_new: false };
        }
        if (message_type === 'sidebar') {
          return { ...rawData, has_new_sidebar_messages: false };
        }
        return rawData;
      };

      // ── 1. Update all cached message-list variants ────────────────────────
      queryClient.setQueriesData({ queryKey: listPartialKey }, (oldData) => {
        if (!oldData?.data?.message) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            message: oldData.data.message.map(listItemPatch),
          },
        };
      });

      // ── 2. Update thread details (raw shape: { data: { ...fields } }) ────
      queryClient.setQueryData(threadKey, (oldData) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: threadDetailsPatch(oldData.data),
        };
      });

      return { previousListQueries, previousThreadDetails, listPartialKey, threadKey };
    },

    onError: (_error, _variables, context) => {
      const { previousListQueries, previousThreadDetails, threadKey } = context || {};

      // Restore each list cache entry individually (preserves per-filter snapshots)
      previousListQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });

      if (threadKey && previousThreadDetails !== undefined) {
        queryClient.setQueryData(threadKey, previousThreadDetails);
      }
    },
  });
}

/**
 * Invite users to a message thread
 */
export function useInviteUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sk, isLead, data }) => inviteUsersToThread(sk, isLead, data),
    onSuccess: (data, variables) => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadDetails.byId(variables?.message_thread_id) });
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadReplies.byId(variables?.message_thread_id) });
      }, 1500);
    },
  });
}

/**
 * Change the case linked to a message
 */
export function useChangeMessageCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pk, data }) => changeMessageCase(pk, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.messages.all });
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadDetails.all });
    },
  });
}

/**
 * Cancel a scheduled message
 */
export function useCancelScheduledMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messagePk, messageSk }) => cancelScheduledMessage(messagePk, messageSk),
    onSuccess: (_data, variables) => {
      const { messageThreadId, messageSk } = variables;
      if (!messageThreadId || !messageSk) {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadReplies.byId(messageThreadId) });
      }
      queryClient.setQueryData(messageQueryKeys.threadReplies.byId(messageThreadId), (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => {
            return {
              ...page,
              data: { ...page.data, replies: page.data.replies.filter((m) => m.sk !== messageSk) },
            };
          }),
        };
      });
    },
  });
}
